import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import { Button } from '@/components/ui/button';
import { TimesheetReminderSettings } from '@/components/TimesheetReminderSettings';
import { ApprovalReminderSettings } from '@/components/ApprovalReminderSettings';
import { MissingTimesheetReminderSettings } from '@/components/MissingTimesheetReminderSettings';
import { ManagerDigestSettings } from '@/components/ManagerDigestSettings';
import { DelegationNotificationSettings } from '@/components/DelegationNotificationSettings';
import {
  OnBehalfOfNotificationSettings,
  DEFAULT_ON_BEHALF_OF_NOTIFICATION_SETTINGS,
} from '@/components/OnBehalfOfNotificationSettings';
import type { OnBehalfOfNotificationSettings as OnBehalfOfNotificationSettingsType } from '@/components/OnBehalfOfNotificationSettings';
import type {
  TimesheetDeadlineSettings as DeadlineSettingsType,
  OrganisationTimesheetSettings,
} from '@/types/timesheetSettings';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import type { DelegationSettings } from '@/features/delegation/types/delegation.types';
import { DEFAULT_DELEGATION_SETTINGS } from '@/features/delegation/types/delegation.types';
import { getDelegationService } from '@/features/delegation/services/delegationService';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { ApiError } from '@/services/apiClient';
import * as settingsRepository from '@/services/settingsRepository';
import { usePermissions } from '@/contexts/PermissionContext';
import { prefixKey } from '@/utils/storagePrefix';

interface NotificationsPageProps {
  onNavigate?: (path: string) => void;
  /**
   * Renders the page for embedding inside another shell (e.g. the onboarding
   * wizard): no PageHeader and no fixed bottom save bar — the host provides
   * its own chrome and triggers saving via `onRegisterSave`.
   */
  embedded?: boolean;
  /** Hands the host a `save()` function so it can persist settings itself. */
  onRegisterSave?: (save: () => Promise<void>) => void;
}

export const NotificationsPage: React.FC<NotificationsPageProps> = ({
  onNavigate: _onNavigate,
  embedded = false,
  onRegisterSave,
}) => {
  const { mode, isDemo, isLive } = useScreenDataMode('settings-notifications');
  const { canManageNotifications } = usePermissions();
  const delegationSvc = getDelegationService(mode);

  const [deadlineSettings, setDeadlineSettings] = useState<DeadlineSettingsType | null>(null);
  const [fullSettings, setFullSettings] = useState<OrganisationTimesheetSettings | null>(null);
  const [rowVersion, setRowVersion] = useState<string | undefined>(undefined);

  const [delegationSettings, setDelegationSettings] = useState<DelegationSettings | null>(null);
  const [isDelegationSettingsValid, setIsDelegationSettingsValid] = useState(true);

  const [onBehalfOfSettings, setOnBehalfOfSettings] = useState<OnBehalfOfNotificationSettingsType>(
    DEFAULT_ON_BEHALF_OF_NOTIFICATION_SETTINGS
  );

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSettingsValid, setIsSettingsValid] = useState(true);

  const handleReminderChange = useCallback((settings: DeadlineSettingsType, isValid: boolean) => {
    setDeadlineSettings(settings);
    setIsSettingsValid(isValid);
    setHasUnsavedChanges(true);
  }, []);

  const handleDelegationChange = useCallback((settings: DelegationSettings, isValid: boolean) => {
    setDelegationSettings(settings);
    setIsDelegationSettingsValid(isValid);
    setHasUnsavedChanges(true);
  }, []);

  const handleOnBehalfOfChange = useCallback((settings: OnBehalfOfNotificationSettingsType) => {
    setOnBehalfOfSettings(settings);
    setHasUnsavedChanges(true);
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    // Always reset stale state before fetching so navigating back from
    // General Settings always shows the latest deadline day/time/grace period
    setDeadlineSettings(null);
    setFullSettings(null);

    try {
      if (isDemo) {
        const stored = localStorage.getItem(prefixKey('demo-timesheet-settings'));
        if (stored) {
          const settings = JSON.parse(stored) as OrganisationTimesheetSettings;
          setFullSettings(settings);
          setDeadlineSettings(settings.deadline);
        } else {
          const defaults = DEFAULT_TIMESHEET_SETTINGS;
          setFullSettings(defaults);
          setDeadlineSettings(defaults.deadline);
        }
        // Load delegation settings (mock or stored)
        const storedDelegation = localStorage.getItem(prefixKey('demo-delegation-settings'));
        if (storedDelegation) {
          setDelegationSettings(JSON.parse(storedDelegation) as DelegationSettings);
        } else {
          setDelegationSettings({ ...DEFAULT_DELEGATION_SETTINGS });
        }
        // Load on-behalf-of notification settings (mock or stored)
        const storedOnBehalfOf = localStorage.getItem(prefixKey('demo-onbehalfof-notification-settings'));
        if (storedOnBehalfOf) {
          setOnBehalfOfSettings(JSON.parse(storedOnBehalfOf) as OnBehalfOfNotificationSettingsType);
        } else {
          setOnBehalfOfSettings({ ...DEFAULT_ON_BEHALF_OF_NOTIFICATION_SETTINGS });
        }
      } else if (isLive) {
        const [settings, delegation] = await Promise.all([
          settingsRepository.getOrganisationTimesheetSettings(mode),
          delegationSvc.getDelegationSettings(),
        ]);
        setFullSettings(settings);
        setRowVersion(settings.rowVersion);
        setDeadlineSettings(settings.deadline);
        setDelegationSettings(delegation);
        setOnBehalfOfSettings({
          notifyOnEntry: settings.onBehalfOf?.notifyOnEntry ?? DEFAULT_ON_BEHALF_OF_NOTIFICATION_SETTINGS.notifyOnEntry,
          notifyOnApproval: settings.onBehalfOf?.notifyOnApproval ?? DEFAULT_ON_BEHALF_OF_NOTIFICATION_SETTINGS.notifyOnApproval,
        });
      }
    } catch (err) {
      console.error('Failed to load notification settings:', err);
      const errorMessage =
        err instanceof ApiError ? err.message : 'Failed to load notification settings';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [isDemo, isLive, mode, delegationSvc]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Browser warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSaveSettings = useCallback(async () => {
    if (!deadlineSettings || !fullSettings) return;
    if (!isSettingsValid || !isDelegationSettingsValid) {
      toast.error('Please fix the highlighted notification settings before saving');
      return;
    }

    setIsSaving(true);
    try {
      // Use deadlineSettings directly — it is the full settings object kept in sync
      // via handleReminderChange. Do NOT spread fullSettings.deadline on top of it
      // as that would overwrite deadlineDay/deadlineTime with stale values loaded
      // at page open, discarding any changes made in General Settings since then.
      const updatedDeadline = deadlineSettings;

      if (isDemo) {
        const updatedSettings: OrganisationTimesheetSettings = {
          ...fullSettings,
          deadline: updatedDeadline,
        };
        localStorage.setItem(prefixKey('demo-timesheet-settings'), JSON.stringify(updatedSettings));
        setFullSettings(updatedSettings);

        if (delegationSettings) {
          localStorage.setItem(prefixKey('demo-delegation-settings'), JSON.stringify(delegationSettings));
        }

        localStorage.setItem(
          prefixKey('demo-onbehalfof-notification-settings'),
          JSON.stringify(onBehalfOfSettings)
        );

        toast.success('Notification settings saved successfully');
      } else if (isLive) {
        const savePromises: Promise<unknown>[] = [
          settingsRepository.updateDeadlineSettings(
            updatedDeadline,
            fullSettings,
            rowVersion,
            mode,
            // Only the notification toggles change here; the rest is sent back as loaded.
            fullSettings.onBehalfOf ? { ...fullSettings.onBehalfOf, ...onBehalfOfSettings } : undefined
          ),
        ];

        if (delegationSettings) {
          savePromises.push(delegationSvc.updateDelegationSettings(delegationSettings));
        }

        const [deadlineResult, delegationResult] = await Promise.all(savePromises) as [
          OrganisationTimesheetSettings & { rowVersion?: string },
          DelegationSettings | undefined,
        ];

        setFullSettings(deadlineResult);
        setRowVersion(deadlineResult.rowVersion);
        setDeadlineSettings(deadlineResult.deadline);

        if (delegationResult) {
          setDelegationSettings(delegationResult);
        }

        toast.success('Notification settings saved successfully');
      }

      setHasUnsavedChanges(false);
      setLastSaved(new Date());
    } catch (err) {
      console.error('Failed to save notification settings:', err);
      const errorMessage =
        err instanceof ApiError ? err.message : 'Failed to save notification settings';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [
    deadlineSettings,
    fullSettings,
    delegationSettings,
    onBehalfOfSettings,
    isDemo,
    isLive,
    isSettingsValid,
    isDelegationSettingsValid,
    mode,
    rowVersion,
    delegationSvc,
  ]);

  useEffect(() => {
    onRegisterSave?.(handleSaveSettings);
  }, [onRegisterSave, handleSaveSettings]);

  // ── Loading state ────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className={embedded ? 'space-y-6' : 'space-y-6 pb-6'}>
        {!embedded && (
          <PageHeader
            title="Notification Settings"
            size={36}
            showBreadcrumb={false}
            icon={<Bell />}
          />
        )}
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className={embedded ? 'space-y-6' : 'space-y-6 pb-6'}>
        {!embedded && (
          <PageHeader
            title="Notification Settings"
            size={36}
            showBreadcrumb={false}
            icon={<Bell />}
          />
        )}
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error}</span>
            <Button variant="outline" size="sm" onClick={loadData}>
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // ── Permission gate ────────────────────────────────────────────────────

  if (!canManageNotifications()) {
    return (
      <div className="space-y-6">
        {!embedded && <PageHeader title="Notification Settings" size={36} />}
        <AccessRestricted message="You don't have permission to access Notification Settings. Contact your administrator to request access." />
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-6 pb-24'}>
      {/* Page Header */}
      {!embedded && (
        <PageHeader
          title="Notification Settings"
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Bell />}
        />
      )}

      {/* Unsaved changes banner */}
      {hasUnsavedChanges && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You have unsaved changes. Click "Save Settings" at the bottom to apply your changes.
          </AlertDescription>
        </Alert>
      )}

      {/* Section 1 — Submission Reminders */}
      {deadlineSettings && (
        <TimesheetReminderSettings
          settings={deadlineSettings}
          onChange={handleReminderChange}
        />
      )}

      {/* Section 2 — Approval Reminders */}
      {deadlineSettings && (
        <ApprovalReminderSettings
          settings={deadlineSettings}
          onChange={handleReminderChange}
        />
      )}

      {/* Section 3 — Missing Timesheet Reminders */}
      {deadlineSettings && (
        <MissingTimesheetReminderSettings
          settings={deadlineSettings}
          onChange={handleReminderChange}
        />
      )}

      {/* Section 4 — Manager Weekly Digest */}
      {deadlineSettings && (
        <ManagerDigestSettings
          settings={deadlineSettings}
          onChange={handleReminderChange}
        />
      )}

      {/* Section 5 — Delegation Notifications */}
      {delegationSettings && (
        <DelegationNotificationSettings
          settings={delegationSettings}
          onChange={handleDelegationChange}
        />
      )}

      {/* Section 6 — On Behalf Of Notifications */}
      <OnBehalfOfNotificationSettings
        settings={onBehalfOfSettings}
        onChange={handleOnBehalfOfChange}
      />

      {/* ── Sticky footer ───────────────────────────────────────────────── */}
      {!embedded && (
      <div className="fixed bottom-0 z-10 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" style={{ left: 'var(--sidebar-width)', right: 0 }}>
        <div className="flex h-16 items-center justify-between gap-4 px-6 max-w-[2000px] mx-auto">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {lastSaved && !hasUnsavedChanges && (
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                Saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
              </span>
            )}
            {hasUnsavedChanges && (
              <span className="flex items-center gap-1.5 text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                Unsaved changes
              </span>
            )}
          </div>
          <Button
            onClick={handleSaveSettings}
            disabled={!hasUnsavedChanges || isSaving || !isSettingsValid || !isDelegationSettingsValid}
            size="lg"
            className="min-w-[120px]"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>
      )}
    </div>
  );
};
